import useSWR from 'swr'

import { FetchError } from '../../../data/fetchError'
import { FetchResponse } from '../../../data/fetchResponse'
import { FetchResult } from '../../../data/fetchResult'
import { fetchJson } from '../../../data/fetchers'
import { TenantConfig } from '../../../data/models'
import { getCurrentTenantConfig } from '../../../utils/tenants'
import { ProjectPermission } from '../types'
import { Permission, ProjectPermissionDto } from './models'

/**
 * Creates a complete URL for permission endpoints including query parameters.
 * The tenantId and projectId are essential for proper permission scoping:
 * - They ensure permissions are fetched for the correct tenant/project context
 * - They prevent permission leakage between different tenants or projects
 * - They maintain strict isolation of permission data in multi-tenant scenarios
 *
 * @param endpoint The base endpoint to use (e.g. 'permissions' or 'permissions/project')
 * @param params TenantConfig object containing tenantId and/or projectId
 * @returns Complete URL with query parameters (e.g. "/permissions?tenantId=123&projectId=456")
 */
const createPermissionsKey = (
  endpoint: string,
  params?: TenantConfig,
): string => {
  const urlParams = new URLSearchParams()

  if (params?.tenantId) urlParams.append('tenantId', params.tenantId)
  if (params?.projectId) urlParams.append('projectId', params.projectId)

  const queryString = urlParams.toString()
  return `/${endpoint}${queryString ? `?${queryString}` : ''}`
}

// TenantGuard
export function useTenantPermissions(options?: {
  revalidateOnFocus?: boolean
  dedupingInterval?: number
}): FetchResult<Permission[]> {
  const { data, error, isLoading, mutate } = useSWR<
    FetchResponse<Permission[]>,
    FetchError
  >('/permissions/tenant', fetchJson<Permission[]>, {
    revalidateOnFocus: options?.revalidateOnFocus ?? true,
    dedupingInterval: options?.dedupingInterval,
  })

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}

export function useTenantProjectPermissions(options?: {
  revalidateOnFocus?: boolean
  dedupingInterval?: number
}): FetchResult<ProjectPermissionDto[]> {
  const currentTenantConfig = getCurrentTenantConfig()

  const { data, error, isLoading, mutate } = useSWR<
    FetchResponse<ProjectPermissionDto[]>,
    FetchError
  >(
    '/permissions/tenant/projectpermissions',
    currentTenantConfig?.tenantId ? fetchJson<ProjectPermissionDto[]> : null,
    {
      revalidateOnFocus: options?.revalidateOnFocus ?? true,
      dedupingInterval: options?.dedupingInterval,
    },
  )

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}

// MicroAppSelection
export function useProjectsByReadPermission(): string[] {
  const viablePermissions: ProjectPermission[] = [
    'webedi:worklist:access',
    'webedi:history:access',
    'webedi:projectuseradmin:access',
    'webedi:projectsettings:access',
    'webedi:history_delfor:access',
    'webedi:worklist_delfor:access',
    'webedi:einvoice:access',
    'webedi:einvoicehistory:access',
  ]
  const { data, error } = useTenantProjectPermissions({
    dedupingInterval: 0,
    revalidateOnFocus: false,
  })

  if (error) throw new Error(error.message)

  const projectsWithPermissions: string[] = []

  data?.forEach(v => {
    if (
      v.permissions.filter(p =>
        viablePermissions.includes(p.name.toLowerCase() as ProjectPermission),
      ).length > 0
    )
      projectsWithPermissions.push(v.project ? v.project : '__tenant')
  })

  return projectsWithPermissions
}

// ProjectGuard
export function useProjectPermissions(options?: {
  revalidateOnFocus?: boolean
  dedupingInterval?: number
}): FetchResult<Permission[]> {
  const currentTenantConfig = getCurrentTenantConfig()
  const key = createPermissionsKey('permissions/project', currentTenantConfig)

  const { data, error, isLoading, mutate } = useSWR<
    FetchResponse<Permission[]>,
    FetchError
  >(key, fetchJson<Permission[]>, {
    revalidateOnFocus: options?.revalidateOnFocus ?? true,
    dedupingInterval: options?.dedupingInterval,
  })

  return {
    data: data?.body,
    isLoading,
    error,
    mutate,
  }
}

// HomeGuard
export function usePermissions(options?: {
  revalidateOnFocus?: boolean
  dedupingInterval?: number
}): FetchResult<Permission[]> {
  const currentTenantConfig = getCurrentTenantConfig()
  const key = createPermissionsKey('permissions', currentTenantConfig)

  const { data, error, isLoading, isValidating, mutate } = useSWR<
    FetchResponse<Permission[]>,
    FetchError
  >(key, fetchJson<Permission[]>, {
    revalidateOnFocus: options?.revalidateOnFocus ?? true,
    dedupingInterval: options?.dedupingInterval,
  })

  return {
    data: data?.body,
    isLoading,
    error,
    isValidating,
    mutate,
  }
}
